import React, { useContext, useEffect } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DrawerLayoutWrapper } from 'common';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import { INTEGER, REQUIRED } from 'utils/helpers/validationMessages';
import { isNumberWithOptionalFormatting } from 'utils/helpers/formValidations';
import { Chip } from '@mui/material';
import { AuthContext } from 'authentication/AuthContext';
import { formatNumberWithCommas, removeLeadingZeros } from 'utils/helpers/format';
import {
  useGetAffiliateProductsQuery,
  useGetDestinationsQuery,
  useGetProductOriginsQuery,
} from '../reconsignmentsEndpoints';
import { useGetAutoReconsignmentfromLocationsQuery } from '../autoReconsignmentsEndpoints';
import renderInput from '../edit/renderUtils';

const VOLUME_ERROR_MESSAGE = value =>
  `Volume must be a whole number greater than or equal to ${value}`;

const AutoConfigurationStep = ({
  next,
  onClose,
  reconsignmentState,
  setComparisonState,
  setReconsignmentState,
  setValidationMessages,
  validationMessages,
}) => {
  const { affiliates } = useContext(AuthContext);
  useEffect(() => {
    if (!R.isEmpty(affiliates) && R.isNil(reconsignmentState.inventoryOwner)) {
      setReconsignmentState(R.assoc('inventoryOwner', affiliates[0]));
      setComparisonState(R.assoc('inventoryOwner', affiliates[0]));
    }
  }, [affiliates, reconsignmentState.inventoryOwner, setReconsignmentState, setComparisonState]);

  const reconParams = {
    inventoryOwner: {
      companyCustomerIdName: R.path(['inventoryOwner', 'customerIdName'], reconsignmentState),
      dataCompanyId: R.path(['inventoryOwner', 'dataCompanyId'], reconsignmentState),
    },
    productId: R.path(['product', 'productId'], reconsignmentState),
  };

  const {
    data: productsQuery = [],
    isSuccess: isSuccessProducts,
    isFetching: isFetchingProducts,
    isError: isErrorProducts,
  } = useGetAffiliateProductsQuery(
    {
      params: {
        inventoryOwner: reconParams.inventoryOwner,
      },
    },
    { skip: R.isNil(reconsignmentState.inventoryOwner) }
  );

  const {
    data: fromLocationsQuery = [],
    isSuccess: isSuccessFromLocations,
    isFetching: isFetchingFromLocation,
    isError: isErrorFromLocation,
  } = useGetAutoReconsignmentfromLocationsQuery(
    { params: reconParams },
    { skip: R.isNil(reconsignmentState.product) }
  );
  const destinationParams = R.assoc(
    'locationId',
    R.path(['fromLocation', 'locationId'], reconsignmentState),
    reconParams
  );

  const {
    data: destinationsQuery = [],
    isSuccess: isSuccessDestinations,
    isFetching: isFetchingDestinations,
  } = useGetDestinationsQuery(
    { params: destinationParams },
    {
      skip: R.isNil(reconsignmentState.fromLocation),
    }
  );

  const fromLocationOptions = isErrorFromLocation ? [] : fromLocationsQuery;
  const toLocationOptions = destinationsQuery;
  const fromLocationOptionsLength = R.length(fromLocationOptions);
  const toLocationOptionsLength = R.length(toLocationOptions);

  const { data: productOriginsQuery = [] } = useGetProductOriginsQuery(
    { params: reconParams },
    { skip: R.isNil(reconsignmentState.product) }
  );

  const validateTransferVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [value => !isNumberWithOptionalFormatting(value), R.always(INTEGER)],
    [R.lt(R.__, 1000), R.always(VOLUME_ERROR_MESSAGE('1,000'))],
    [R.T, R.always('')],
  ]);

  const validateThresholdVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [value => !isNumberWithOptionalFormatting(value), R.always(INTEGER)],
    [R.lt(R.__, 200), R.always(VOLUME_ERROR_MESSAGE('200'))],
    [R.T, R.always('')],
  ]);

  const sanitizeVolume = R.compose(R.replace(/[^0-9]/g, ''), R.path(['target', 'value']));

  const handleTransferVolumeChange = R.compose(
    setReconsignmentState,
    R.assoc('transferVolume'),
    R.tap(R.compose(setValidationMessages, R.assoc('transferVolume'), validateTransferVolume)),
    removeLeadingZeros,
    sanitizeVolume
  );

  const handleThresholdVolumeChange = R.compose(
    setReconsignmentState,
    R.assoc('thresholdVolume'),
    R.tap(R.compose(setValidationMessages, R.assoc('thresholdVolume'), validateThresholdVolume)),
    removeLeadingZeros,
    sanitizeVolume
  );
  const handleToLocationChange = R.compose(setReconsignmentState, R.assoc('toLocation'));
  const handleFromLocationChange = R.compose(
    setReconsignmentState,
    R.tap(() => handleToLocationChange(null)),
    R.assoc('fromLocation')
  );

  const handleProductChange = R.compose(
    setReconsignmentState,
    R.assoc('product'),
    R.tap(() => handleFromLocationChange(null)),
    R.tap(() => handleToLocationChange(null)),
    R.tap(() => R.compose(setReconsignmentState, R.assoc('origins'))(null))
  );

  const handleInventoryOwnerChange = R.compose(
    setReconsignmentState,
    R.assoc('inventoryOwner'),
    R.tap(() => handleProductChange(null))
  );

  const handleOriginChange = R.when(
    _ => R.lte(R.length(_), 3),
    R.compose(setReconsignmentState, R.assoc('origins'))
  );

  const allEmpty = R.pipe(R.values, R.all(R.isEmpty));

  const isInvalid = [
    R.not(allEmpty(validationMessages)),
    R.isNil(reconsignmentState.inventoryOwner),
    R.isNil(reconsignmentState.product),
    R.isNil(reconsignmentState.fromLocation),
    R.isNil(reconsignmentState.toLocation),
    R.isNil(reconsignmentState.thresholdVolume),
    R.isNil(reconsignmentState.transferVolume),
  ].includes(true);

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={onClose} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Review
      </Button>
    </Box>
  );

  const renderTags = (tags, getTagProps) =>
    tags.map((item, index) => (
      <Chip
        label={item.originName}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
        {...getTagProps({ index })}
      />
    ));

  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onClose={onClose}
      onHeaderButtonClick={onClose}
      title="New Auto Reconsignment"
    >
      <Box
        sx={{
          borderBottom: 1,
          borderBottomColor: 'graphic.light',
          p: 3,
          width: '100%',
        }}
      >
        <LinearProgress data-variant="magellanProgressBar" value={50} variant="determinate" />
        <Typography>Configuration (1 of 2)</Typography>
      </Box>
      <Stack sx={{ display: 'grid', gap: 3, m: 3 }}>
        {!(R.isEmpty(affiliates) || R.equals(R.length(affiliates), 1)) && (
          <Autocomplete
            disableClearable
            getOptionLabel={R.prop('customerNameShort')}
            isOptionEqualToValue={isOptionEqualToValue('customerIdName')}
            onChange={(_, value) => handleInventoryOwnerChange(value)}
            options={affiliates}
            renderInput={params => <TextField {...params} label="Inventory Owner" />}
            value={reconsignmentState.inventoryOwner}
          />
        )}
        <Autocomplete
          disableClearable
          disabled={!(isSuccessProducts || isErrorProducts) || isFetchingProducts} // api returns code 500 sometimes so this is a workaround
          getOptionLabel={R.prop('productName')}
          isOptionEqualToValue={R.eqProps('productId')}
          onChange={(_, value) => handleProductChange(value)}
          options={isSuccessProducts ? productsQuery : []} // api returns code 500 sometimes so this is a workaround
          renderInput={params => <TextField {...params} label="Product" />}
          value={reconsignmentState.product}
        />
        <Autocomplete
          disableClearable
          disabled={!(isSuccessFromLocations || isErrorFromLocation) || isFetchingFromLocation} // api returns code 500 for some products so this is a workaround
          getOptionLabel={R.prop('locationName')}
          isOptionEqualToValue={R.eqProps('locationId')}
          onChange={(_, value) => handleFromLocationChange(value)}
          options={isSuccessFromLocations ? fromLocationsQuery : []} // api returns code 500 for some products so this is a workaround
          renderInput={renderInput({
            label: 'From Location',
            length: fromLocationOptionsLength,
            loading: isFetchingFromLocation,
            showNoOptions: !R.isNil(reconsignmentState.product),
          })}
          value={reconsignmentState.fromLocation}
        />
        <Autocomplete
          disableClearable
          disabled={!isSuccessDestinations || R.isNil(reconsignmentState.fromLocation)}
          getOptionLabel={R.prop('locationName')}
          isOptionEqualToValue={R.eqProps('locationId')}
          onChange={(_, value) => handleToLocationChange(value)}
          options={destinationsQuery}
          renderInput={renderInput({
            label: 'To Location',
            length: toLocationOptionsLength,
            loading: isFetchingDestinations,
            showNoOptions: !R.isNil(reconsignmentState.fromLocation),
          })}
          value={reconsignmentState.toLocation}
        />
        <TextField
          error={R.not(R.isEmpty(validationMessages.thresholdVolume))}
          helperText={validationMessages.thresholdVolume}
          label="Threshold Volume"
          onChange={handleThresholdVolumeChange}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
            },
          }}
          value={formatNumberWithCommas(R.defaultTo('', reconsignmentState.thresholdVolume))}
        />
        <TextField
          error={R.not(R.isEmpty(validationMessages.transferVolume))}
          helperText={validationMessages.transferVolume}
          label="Transfer Volume"
          onChange={handleTransferVolumeChange}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
            },
          }}
          value={formatNumberWithCommas(R.defaultTo('', reconsignmentState.transferVolume))}
        />
        <Autocomplete
          disableClearable
          getOptionLabel={R.prop('originName')}
          isOptionEqualToValue={isOptionEqualToValue('originId')}
          multiple
          onChange={(_, value) => handleOriginChange(value)}
          options={productOriginsQuery}
          renderInput={params => (
            <TextField {...params} helperText="Optional, select up to 3 origins" label="Origin" />
          )}
          renderTags={renderTags}
          value={reconsignmentState.origins ?? []}
        />
      </Stack>
    </DrawerLayoutWrapper>
  );
};

AutoConfigurationStep.propTypes = {
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({}),
    inventoryOwner: PropTypes.shape({
      customerIdName: PropTypes.string,
      dataCompanyId: PropTypes.string,
    }),
    origins: PropTypes.arrayOf(PropTypes.shape({})),
    product: PropTypes.shape({}),
    thresholdVolume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    toLocation: PropTypes.shape({}),
    transferVolume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setComparisonState: PropTypes.func.isRequired,
  setReconsignmentState: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  validationMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default AutoConfigurationStep;
